import React, { useCallback, useState, useMemo } from "react";
import { SafetyCertificateFilled } from "@ant-design/icons";
import { Alert, Button, Space, Typography, message } from "antd";
import { connect } from "dva";
import { LoadStatus, SECURE_PASS_TAG_STATUS, ShipmentStatus } from "@/utils/enum";
import { getUrlParams } from "@/utils/utils";
import { resendSecurePass } from "@/services/secure_pass";
import SecurePassAdminOverridePopup from "@/components/SecurePass/SecurePassAdminOverridePopup/SecurePassAdminOverridePopup";
import SecurePassText from "@/components/SecurePass/SecurePassText/SecurePassText";
import TooltipWrapper from "@/components/TooltipWrapper/TooltipWrapper";

const { Paragraph } = Typography;

const DisabledWarningDescription = () => (
  <Paragraph>
    This shipment is not setup to use Autosled <SecurePassText />. Please consider enabling SecurePass for additional
    safety. See{" "}
    <a href="https://autosled.com/SecurePass" target="_blank" rel="noopener noreferrer">
      here
    </a>
  </Paragraph>
);

const EnabledMissingEmailDescription = () => (
  <Paragraph>
    This shipment is setup to enable use of Autosled <SecurePassText />, but no email has been set for the pickup
    contact. Please add a valid email address for the pickup contact prior to posting.
  </Paragraph>
);

const EnabledSuccessDescription = ({ contactEmail }) => (
  <>
    <Paragraph>
      This shipment is setup to enable use of Autosled <SecurePassText />.
    </Paragraph>

    <Paragraph>
      Pickup contact,{" "}
      <b>
        <TooltipWrapper text={contactEmail} />
      </b>
      , will receive email instructions upon posting.
    </Paragraph>
  </>
);

const EmptyStateDescription = () => (
  <Paragraph>
    <SecurePassText /> Information will be available once the shipment has been posted.
  </Paragraph>
);

const alertStyle = { padding: 4 };

const SecurePassAlert = ({
  contactEmail,
  securePassValue,
  configPerm,
  shipmentStatus,
  securePassEnabled,
  loadStatus,
  onResendSuccess,
}) => {
  const shipmentId = getUrlParams("shipmentId");
  const [adminOverridePopupVisible, setAdminOverridePopupVisible] = useState(false);
  const [loadingResend, setLoadingResend] = useState(false);

  const canResend = useMemo(
    () =>
      securePassEnabled && // feature flag enabled
      securePassValue === SECURE_PASS_TAG_STATUS.Enabled && // secure pass enabled
      !!configPerm["CoreService.AdminShipments.SecurePassResend"] &&
      [ShipmentStatus.Posted, ShipmentStatus.Assigned].includes(shipmentStatus) &&
      shipmentStatus === ShipmentStatus.Assigned
        ? loadStatus === LoadStatus.Active
        : true,
    [configPerm, securePassEnabled, shipmentStatus, securePassValue, loadStatus],
  );

  const canByPassConfirmation = useMemo(
    () =>
      securePassEnabled && // feature flag enabled
      securePassValue === SECURE_PASS_TAG_STATUS.Enabled && // secure pass enabled
      !!configPerm["CoreService.AdminShipments.SecurePassByPassConfirmation"] &&
      shipmentStatus === ShipmentStatus.Assigned &&
      loadStatus === LoadStatus.Active,
    [configPerm, securePassEnabled, shipmentStatus, securePassValue, loadStatus],
  );

  const onClickAdminOverride = useCallback(() => {
    setAdminOverridePopupVisible(true);
  }, []);

  const handleSecurePassResend = useCallback(async () => {
    const hide = message.loading("Sending Secure Pass...");

    try {
      setLoadingResend(true);
      await resendSecurePass(shipmentId);
      hide();
      message.success("SecurePass sent to pickup contact.");
      onResendSuccess?.();
    } catch (error) {
      hide();
      console.error(error);
      message.error("Could not resend Secure Pass");
    } finally {
      setLoadingResend(false);
    }
  }, [shipmentId, onResendSuccess]);

  const securePassContent = {
    [SECURE_PASS_TAG_STATUS.Disabled]: (
      <Alert
        message={
          <span>
            <SecurePassText /> Disabled
          </span>
        }
        description={<DisabledWarningDescription />}
        type="warning"
        style={alertStyle}
        showIcon
      />
    ),
    [SECURE_PASS_TAG_STATUS.MissingContact]: (
      <Alert
        message={
          <span>
            Finish <SecurePassText /> Setup
          </span>
        }
        description={<EnabledMissingEmailDescription />}
        type="warning"
        showIcon
        style={alertStyle}
      />
    ),
    [SECURE_PASS_TAG_STATUS.Enabled]: (
      <>
        <Alert
          message={
            <Space>
              <SafetyCertificateFilled className="ant-tag-success" />
              <Paragraph style={{ marginBottom: 0 }}>
                <SecurePassText /> Enabled
              </Paragraph>
            </Space>
          }
          description={
            <>
              <EnabledSuccessDescription contactEmail={contactEmail} />

              <Space>
                {canResend && (
                  <Button
                    key="secure-pass-resend"
                    size="small"
                    type="primary"
                    onClick={handleSecurePassResend}
                    loading={loadingResend}
                  >
                    RESEND
                  </Button>
                )}
                {canByPassConfirmation && (
                  <Button key="secure-pass-override" size="small" type="secondary" onClick={onClickAdminOverride}>
                    OVERRIDE
                  </Button>
                )}
              </Space>
            </>
          }
          type="success"
          style={alertStyle}
        />
        <SecurePassAdminOverridePopup
          visible={adminOverridePopupVisible}
          onClose={() => setAdminOverridePopupVisible(false)}
        />
      </>
    ),
    // This is the case when the shipment is not posted yet
    null: (
      <Alert
        message={
          <span>
            <SecurePassText /> Information
          </span>
        }
        description={<EmptyStateDescription />}
        type="info"
        style={{ padding: 12 }}
        showIcon
      />
    ),
  };

  return securePassContent[securePassValue];
};

export default connect(({ configs }) => ({
  configPerm: configs.configPerm,
  securePassEnabled: configs.securePass.enabled,
}))(SecurePassAlert);
